import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import SourceLogo from './SourceLogo';
import Dialog from '../../ui/Dialog';
import ErrorState from '../../ui/ErrorState';
import { formatUtcDateTime, formatUtcTime } from '../../lib/time';
import {
  sourceDisplayName,
  sourceLogoExtension,
  sourceLogoSlug,
  type SourceItem,
} from '../../api-utils/sources';
import { useDataClock } from '../../data';
import { combine } from '../../data/queries/combine';
import { useSources, useThreatCandidates } from '../../data/queries/hooks';
import { isWithinFunnelWindow, sourceRefId, DEFAULT_FUNNEL_WINDOW, funnelWindowLabel, type FunnelWindow } from '../../lib/funnel';

interface ConnectedSourcesTabProps {
  window?: FunnelWindow;
  onOpenConnectors?: () => void;
  onAddSource?: () => void;
}

function formatClock(iso: string | undefined): string {
  return formatUtcTime(iso);
}

interface HealthLine {
  alertsInWindow: number;
  lastEvent: string;
  lastClock: string;
  lag: string;
  cadence: string;
}

function formatLag(iso: string | undefined, nowMs: number): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '—';
  const delta = Math.max(0, nowMs - ms);
  const mins = Math.round(delta / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

function formatWhen(iso: string | undefined): string {
  return formatUtcDateTime(iso);
}

const STATUS_WORDS: Record<string, string> = {
  active: 'Active',
  inactive: 'Inactive',
  error: 'Error',
  available: 'Available',
};

/** What a configured source reads as on its tile. A source that is not active at all (inactive) has no tile. */
type SourceTone = 'connected' | 'impaired' | 'disconnected';

const TONE_WORDS: Record<SourceTone, string> = {
  connected: 'Connected',
  impaired: 'Impaired',
  disconnected: 'Disconnected',
};

function sourceTone(source: SourceItem): SourceTone | null {
  const status = String(source.status || '').toLowerCase();
  if (status === 'disconnected') return 'disconnected';
  if (status === 'error') return 'impaired';
  if (status !== 'active') return null;
  return String(source.last_error || '').trim() ? 'impaired' : 'connected';
}

const EMPTY_HEALTH: HealthLine = {
  alertsInWindow: 0,
  lastEvent: '—',
  lastClock: '—',
  lag: '—',
  cadence: '—',
};

const ConnectedSourcesTab: React.FC<ConnectedSourcesTabProps> = ({
  window: funnelWindow = DEFAULT_FUNNEL_WINDOW,
  onOpenConnectors,
  onAddSource,
}) => {
  // The sources and the alerts come from the shared cache, so this tab, the band and the shell read the same records.
  const { refreshNow } = useDataClock();
  const sourcesRead = useSources();
  const candidatesRead = useThreatCandidates();
  const read = combine([sourcesRead, candidatesRead]);
  const sourceRows = sourcesRead.data;
  const sources: SourceItem[] = useMemo(() => (read.failed ? [] : sourceRows ?? []), [read.failed, sourceRows]);
  const loading = read.loading;
  const error = read.error ? 'Could not load the connected sources' : null;
  const [healthSource, setHealthSource] = useState<{
    name: string;
    health: HealthLine;
    status: string;
  } | null>(null);

  // Counting looks back from the moment the newest read stands for, as the Overview counters do.
  const countedAt = read.updatedAt;
  const candidates = candidatesRead.data;
  const healthByKey = useMemo(() => {
    const nowMs = countedAt || Date.now();
    const grouped: Record<string, { count: number; last?: string }> = {};
    for (const candidate of candidates ?? []) {
      const detected = candidate.detected_at || candidate.processed_at || '';
      if (!isWithinFunnelWindow(detected, nowMs, funnelWindow)) continue;
      const keys = [sourceRefId(candidate.source_id), String(candidate.source || '').toLowerCase()].filter(Boolean);
      for (const key of keys) {
        const entry = grouped[key] || { count: 0 };
        entry.count += 1;
        if (!entry.last || Date.parse(detected) > Date.parse(entry.last)) entry.last = detected;
        grouped[key] = entry;
      }
    }
    const health: Record<string, HealthLine> = {};
    for (const [key, entry] of Object.entries(grouped)) {
      health[key] = {
        alertsInWindow: entry.count,
        lastEvent: formatWhen(entry.last),
        lastClock: formatClock(entry.last),
        lag: formatLag(entry.last, nowMs),
        cadence: '—',
      };
    }
    return health;
  }, [candidates, countedAt, funnelWindow]);

  const tiles = useMemo(() => {
    return sources
      .flatMap((source) => {
        const tone = sourceTone(source);
        if (!tone) return [];
        const connected = tone !== 'disconnected';
        const health =
          healthByKey[source._id] ||
          healthByKey[String(source.candidate_source || '').toLowerCase()] ||
          healthByKey[String(source.provider || '').toLowerCase()] ||
          EMPTY_HEALTH;
        return [
          {
            id: source._id,
            name: sourceDisplayName(source),
            provider: source.provider || '',
            connected,
            tone,
            status: String(source.status || 'inactive'),
            health,
          },
        ];
      })
      .sort((a, b) => {
        if (a.connected !== b.connected) return a.connected ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
  }, [sources, healthByKey]);

  const connected = tiles.filter((t) => t.connected).length;
  const impaired = tiles.filter((t) => t.tone === 'impaired').length;
  const addSource = () => {
    if (onAddSource) onAddSource();
    else onOpenConnectors?.();
  };

  return (
    <div>
      <div className="c3-src-head">
        <h2>Connected Sources</h2>
      </div>
      {!error && (
        <p className="c3-src-meta">
          {loading
            ? 'Loading sources…'
            : `${connected} connected${impaired > 0 ? ` · ${impaired} impaired` : ''} · cadence —`}
        </p>
      )}
      {error && (
        <ErrorState title={error} onRetry={() => void refreshNow()}>
          Nothing was changed. Try again in a moment.
        </ErrorState>
      )}
      {!error && (
        <div className="c3-src-legend" aria-label="Source status legend">
          <span>
            <span className="c3-src-dot" />
            Connected
          </span>
          <span>
            <span className="c3-src-dot is-error" />
            Impaired
          </span>
          <span>
            <span className="c3-src-dot is-off" />
            Disconnected
          </span>
          <span>
            <span className="c3-src-dot is-avail" />
            Available
          </span>
        </div>
      )}
      <div className="c3-src-grid">
        {tiles.map((tile) => {
          const slug = sourceLogoSlug({
            provider: tile.provider,
            name: tile.name,
            candidate_source: tile.provider,
          });
          const ext = sourceLogoExtension(slug);
          const statusLabel = TONE_WORDS[tile.tone];
          const toneCls = tile.tone === 'impaired' ? ' is-error' : tile.tone === 'disconnected' ? ' is-off' : '';
          const alerts = tile.health.alertsInWindow.toLocaleString('en-US');
          return (
            <button
              key={tile.id}
              type="button"
              className={`c3-src-tile${tile.tone === 'impaired' ? ' is-error' : ''}${tile.tone === 'disconnected' ? ' is-off' : ''}`}
              onClick={() => {
                if (tile.connected) {
                  setHealthSource({
                    name: tile.name,
                    health: tile.health,
                    status: tile.status,
                  });
                } else {
                  onOpenConnectors?.();
                }
              }}
            >
              <div className="c3-src-id">
                <div className="c3-src-logo">
                  <SourceLogo slug={slug} ext={ext} name={tile.name} />
                </div>
                <div className="c3-src-ident">
                  <div className="c3-src-name" title={tile.name}>
                    {tile.name}
                  </div>
                  <div className={`c3-src-status${toneCls}`}>
                    <span className={`c3-src-dot${toneCls}`} />
                    {statusLabel}
                    {tile.connected && <span className="c3-src-cadence">· Cadence {tile.health.cadence}</span>}
                  </div>
                </div>
              </div>
              {tile.connected && (
                <dl className="c3-src-stats">
                  <div>
                    <dt>Lag</dt>
                    <dd className={tile.health.lag === '—' ? 'is-empty' : undefined}>{tile.health.lag}</dd>
                  </div>
                  <div>
                    <dt>Alerts {funnelWindowLabel(funnelWindow)}</dt>
                    <dd>{alerts}</dd>
                  </div>
                  <div>
                    <dt>Last event</dt>
                    <dd className={tile.health.lastClock === '—' ? 'is-empty' : undefined}>{tile.health.lastClock}</dd>
                  </div>
                </dl>
              )}
            </button>
          );
        })}
        {!loading && (
          <button type="button" className="c3-src-tile is-add" onClick={addSource}>
            <span className="c3-src-add-icon">
              <Plus size={20} strokeWidth={1.5} aria-hidden />
            </span>
            <span className="c3-src-add-label">Connect New Source</span>
          </button>
        )}
      </div>

      {healthSource && (
        <Dialog size="sm" title="Integration Health" onClose={() => setHealthSource(null)}>
          <p className="text-sm mb-3">{healthSource.name}</p>
          <dl className="text-[13px] space-y-2">
            <div className="flex justify-between">
              <dt className="text-c3-mute">Status</dt>
              <dd>{STATUS_WORDS[healthSource.status] ?? 'Unknown'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-c3-mute">Alerts ({funnelWindowLabel(funnelWindow)})</dt>
              <dd>{healthSource.health.alertsInWindow}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-c3-mute">Last event</dt>
              <dd>{healthSource.health.lastEvent}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-c3-mute">Lag</dt>
              <dd>{healthSource.health.lag}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-c3-mute">Cadence</dt>
              <dd title="Source cadence is not recorded yet">—</dd>
            </div>
          </dl>
        </Dialog>
      )}
    </div>
  );
};

export default ConnectedSourcesTab;
